import { Chart as ChartJS, LegendItem, UpdateMode } from 'chart.js';
import { AnyObject } from 'chart.js/dist/types/basic';
import React from 'react';

export type ChartXType = 'date' | 'text';

export const barChartColors =
	// blue100, blue300
	['#3583F8', '#BFD9FD'];

export const colors = [
	// red100, orange100, yellow100, green100, blue100, purple100
	'#ED6D85',
	'#F2A354',
	'#F7CF6B',
	'#6CBEBF',
	'#56A0E5',
	'#9169F6',
];

export interface GraphData {
	label?: string;
	kind?: ChartXType;
	xValues?: string[];
	yValues?: number[];
}

export type FormatStyle = 'percentage' | 'value' | 'currency';

export const formattedValue = (formatStyle: FormatStyle, value?: any) => {
	if (value && typeof value === 'number') {
		switch (formatStyle) {
			case 'percentage':
				return `${value}%`;
			case 'currency':
				return `$${value}`;
			case 'value':
			default:
				return `${value}`;
		}
	}
	return '';
};

// -----------------------------------------------------------------
// @see https://www.chartjs.org/docs/latest/samples/legend/html.html
// -----------------------------------------------------------------
export const getOrCreateLegendList = (chart: ChartJS, id: string) => {
	const legendContainer = document.getElementById(id);
	let listContainer = legendContainer?.querySelector('ul');

	if (!listContainer) {
		listContainer = document.createElement('ul');
	}

	return listContainer;
};

interface AfterUpdateParams {
	legends?: string[];
	includeXValues?: boolean;
}
export const afterUpdateFn =
	({ legends, includeXValues = false }: AfterUpdateParams) =>
	(chart: ChartJS, args: { mode: UpdateMode; cancelable: true }, options: AnyObject) => {
		const ul = getOrCreateLegendList(chart, options.containerID);

		// Remove old legend items
		while (ul.firstChild) {
			ul.firstChild.remove();
		}

		// Reuse the built-in legendItems generator
		const items = chart?.options?.plugins?.legend?.labels?.generateLabels?.(chart);
		const xValues = chart?.data?.datasets?.[0]?.data;

		items?.forEach((item: LegendItem, index: number) => {
			const li = document.createElement('li');

			// Color box
			const boxSpan = document.createElement('span');
			boxSpan.style.background = item.fillStyle?.toString() ?? '';
			boxSpan.style.borderColor = item.strokeStyle?.toString() ?? '';

			// Text
			const textContainer = document.createElement('p');
			textContainer.style.textDecoration = item.hidden ? 'line-through' : '';

			const text = document.createTextNode(
				includeXValues
					? `${item.text} - ${formattedValue(options.formatStyle, xValues?.[index] || undefined)}`
					: legends?.[index] ?? item.text
			);
			textContainer.appendChild(text);

			li.appendChild(boxSpan);
			li.appendChild(textContainer);
			ul.appendChild(li);
		});
	};

export const getMultipleDatasets = (chartOrGraph: Queries.ContentfulChartOrGraph): GraphData[] => {
	const data = chartOrGraph.datasets?.flatMap((set) => set ?? []);
	return (
		data?.map((set) => {
			const { x, y } = set?.dataset ?? {};
			const xValues = x?.values?.flatMap((v) => v ?? '');
			const yValues = y?.values?.flatMap((v) => v ?? 0);
			const kind = (x?.kind as ChartXType) ?? 'text';
			return { label: y?.label || '', kind, xValues, yValues };
		}) ?? []
	);
};

export const getSingleDataset = (chartOrGraph: Queries.ContentfulChartOrGraph): GraphData => {
	const dataset = chartOrGraph.datasets?.[0];
	const { x, y } = dataset?.dataset ?? {};
	const xValues = x?.values?.flatMap((v) => v ?? '');
	const yValues = y?.values?.flatMap((v) => v ?? 0);
	return { label: y?.label ?? '', xValues, yValues };
};
